import { Injectable } from "@angular/core";
import { ComponentStore } from "@ngrx/component-store";
import { Observable } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { UpdateNewsletterTemplateState } from "./update-newsletter-template.state";
import { CreateNewsletterTemplateModel } from "src/app/models/create-newsletter-template-model";
import { NewsletterTemplateModel } from "src/app/models/newsletter-template-model";
import { NewsletterTemplateService } from "src/app/services/newsletter-template.service";
import { UpdateNewsletterTemplateModel } from "src/app/models/update-newsletter-template-model";

const initialState: UpdateNewsletterTemplateState = {
    loading: false,
    loaded: false,
    success: false,
    errorMessage: null,
    updateNewsletterTemplate: null,
    newsletterTemplate: null,
};

@Injectable({
    providedIn: 'root'
})
export class UpdateNewsletterTemplateComponentStore extends ComponentStore<UpdateNewsletterTemplateState> {

    constructor(private readonly newsletterTemplateService: NewsletterTemplateService) {
        super(initialState);
    }

    readonly updateNewsletterTemplate$: Observable<CreateNewsletterTemplateModel> = this.select(state => state.updateNewsletterTemplate);
    readonly newsletterTemplate$: Observable<NewsletterTemplateModel> = this.select(state => state.newsletterTemplate);
    readonly loading$: Observable<boolean> = this.select(state => state.loading);
    readonly loaded$: Observable<boolean> = this.select(state => state.loaded);
    readonly success$: Observable<boolean> = this.select(state => state.success);
    readonly errorMessage$: Observable<any> = this.select(state => state.errorMessage);

    readonly viewModel$ = this.select(
        this.updateNewsletterTemplate$,
        this.newsletterTemplate$,
        this.loaded$,
        this.loading$,
        this.success$,
        this.errorMessage$,
        (updateNewsletterTemplateModel, newsletterTemplate, loaded, loading, success, errorMessage) => ({
            updateNewsletterTemplateModel, newsletterTemplate, loaded, loading, success, errorMessage
        })
    );

    readonly setInitial = this.updater(( _ : UpdateNewsletterTemplateState) => {
        return {
            ...initialState
        };
    });

    readonly setLoading = this.updater((state: UpdateNewsletterTemplateState) => {
        return {
            ...state,
            loading: true,
            loaded: false,
        };
    });

    readonly setLoaded = this.updater((state: UpdateNewsletterTemplateState) => {
        return {
            ...state,
            loading: false,
            loaded: true,
        };
    });

    readonly updateUpdateNewsletterTemplateState = this.updater((state: UpdateNewsletterTemplateState, value: {
        updateNewsletterTemplate: CreateNewsletterTemplateModel
    }) => {
        return {
            ...state,
            loading: false,
            loaded: true,
            success: true,
            createNewsletterTemplate: value.updateNewsletterTemplate
        };
    });

    readonly updateNewsletterTemplateState = this.updater((state: UpdateNewsletterTemplateState, value: {
        newsletterTemplate: NewsletterTemplateModel
    }) => {
        return {
            ...state,
            loading: false,
            loaded: true,
            success: true,
            newsletterTemplate: value.newsletterTemplate
        };
    });

    readonly setError = this.updater((state: UpdateNewsletterTemplateState, value: {
        errorMessage: String
    }) => {
        return {
            ...state,
            loading: false,
            loaded: true,
            success: false,
            errorMessage: value.errorMessage,
        };
    });

    readonly updateNewsletterTemplate = this.effect((params$: Observable<{ companyId: string, updateNewsletterTemplate: UpdateNewsletterTemplateModel }>) => {
        this.setLoading();
        return params$.pipe(
            switchMap((params => this.newsletterTemplateService.updateNewsletterTemplate(params.companyId, params.updateNewsletterTemplate).pipe(
                map((newsletterTemplate) => {
                    if(newsletterTemplate === null || newsletterTemplate === undefined){
                        this.setError({ errorMessage: "Can't create a new newsletter template." });
                        this.setLoaded();
                        this.setInitial();
                    }
                    else {
                        this.updateNewsletterTemplateState({ newsletterTemplate: newsletterTemplate });
                        this.updateUpdateNewsletterTemplateState({updateNewsletterTemplate : params.updateNewsletterTemplate })
                        this.setLoaded();
                        this.setInitial();
                    }
                }),
                catchError(async (error) => {
                    this.setError({ errorMessage: error });
                    this.setLoaded();
                    this.setInitial();
                })
            ))
            )
        );
    });
}